import { useEffect, useState, useMemo } from "react";
import api, { fmtBizDateTime, BUSINESS_TZ } from "@/lib/api";
import AppShell from "@/components/AppShell";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from "@/components/ui/dialog";
import { Trash2, ImageOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const REFRESH_MS = 30_000; // auto-refresh so new screenshots appear live

export default function ScreenshotsPage({ scope = "admin" }) {
  // scope: "admin" or "client"
  const [users, setUsers] = useState([]);
  const [shots, setShots] = useState([]);
  const [userId, setUserId] = useState("all");
  const [date, setDate] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    const path = scope === "client" ? "/client/users" : "/admin/users";
    api.get(path).then((r) => {
      const list = scope === "client" ? r.data : r.data.filter((u) => u.role === "employee");
      setUsers(list);
    });
  }, [scope]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (userId !== "all") params.user_id = userId;
      if (date) params.date = date;
      const { data } = await api.get("/screenshots", { params });
      // Backend returns DESC (newest first). User wants chronological — oldest → newest.
      const sorted = [...data].sort((a, b) => new Date(a.captured_at) - new Date(b.captured_at));
      setShots(sorted);
      setLastRefresh(new Date());
    } catch (e) {
      toast.error("Failed to load screenshots — retrying next cycle");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId, date]);

  // Auto-refresh so new screenshots appear live without a manual reload
  useEffect(() => {
    const t = setInterval(() => { load(); }, REFRESH_MS);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [userId, date]);

  const userMap = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const remove = async (id) => {
    if (!window.confirm("Delete screenshot?")) return;
    try { await api.delete(`/screenshots/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <AppShell>
      <div className="p-6 lg:p-10 max-w-7xl">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-bold text-stone-500 mb-1">[ {scope === "client" ? "Client" : "Admin"} / Screenshots ]</div>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Screenshot Monitor</h1>
            <div className="text-[11px] text-stone-500 mt-1 font-mono-num">
              All times shown in <span className="font-bold">{BUSINESS_TZ}</span> · oldest → newest · auto-refresh every {REFRESH_MS / 1000}s
            </div>
          </div>
          <Button
            data-testid="refresh-screenshots-button"
            variant="outline"
            onClick={load}
            className="rounded-md border-stone-300 shrink-0"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Refresh
            {lastRefresh && (
              <span className="ml-2 text-[10px] tracking-[0.15em] uppercase text-stone-400 font-mono-num">
                {lastRefresh.toLocaleTimeString("en-PH", { timeZone: BUSINESS_TZ, hour: "2-digit", minute: "2-digit" })}
              </span>
            )}
          </Button>
        </div>

        {/* Filters */}
        <div className="border border-stone-200 p-4 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 bg-stone-50">
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Employee</Label>
            <Select value={userId} onValueChange={setUserId}>
              <SelectTrigger className="rounded-md mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="rounded-md">
                <SelectItem value="all">All employees</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs tracking-[0.2em] uppercase font-bold">Date</Label>
            <Input type="date" className="rounded-md mt-1 bg-white font-mono-num" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="flex items-end">
            <Button variant="outline" onClick={() => { setUserId("all"); setDate(""); }} className="rounded-md border-stone-300">
              Clear filters
            </Button>
          </div>
        </div>

        {loading && shots.length === 0 ? (
          <div className="text-center py-12 text-sm text-stone-500">Loading…</div>
        ) : shots.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-stone-200">
            <ImageOff className="h-10 w-10 mx-auto text-stone-300 mb-3" />
            <div className="text-sm text-stone-500">No screenshots found.</div>
            <div className="text-xs text-stone-400 mt-1">Screenshots will appear when employees run the tracker with screen sharing.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {shots.map((s) => (
              <div key={s.id} data-testid={`screenshot-tile-${s.id}`} className="bg-white group">
                <button onClick={() => setOpen(s)} className="block w-full aspect-video bg-stone-100 overflow-hidden">
                  <img src={s.image_base64} alt="screenshot" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                </button>
                <div className="p-3 text-xs">
                  <div className="font-bold truncate">{s.user_name || userMap[s.user_id]?.name || "User"}</div>
                  <div className="font-mono-num text-stone-500 mt-0.5" data-testid={`screenshot-time-${s.id}`}>
                    {fmtBizDateTime(s.captured_at)}
                  </div>
                  {scope === "admin" && (
                    <button onClick={() => remove(s.id)} className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase font-bold text-[#a8421f] hover:underline">
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="rounded-md max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display text-lg tracking-tight">
              {open?.user_name || "Screenshot"}
              <div className="text-xs font-mono-num text-stone-500 mt-1 font-normal">
                {open && fmtBizDateTime(open.captured_at)}
              </div>
            </DialogTitle>
          </DialogHeader>
          {open && <img src={open.image_base64} alt="full" className="w-full h-auto border border-stone-200" />}
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
